import { useEffect, useState } from 'react';
import {
  deleteSiteEmail,
  getSiteEmail,
  saveSiteEmail,
  siteEmailError,
  testSiteEmail,
  type SiteEmailInput,
  type SiteEmailSettings,
  type SmtpSecurity,
} from '../../lib/site-email-api';
import { useAgentStore } from '../../stores/agent';
import styles from './admin.module.css';
import { confirmDialog } from '../../stores/confirm';
import { SecretInput } from './SecretInput';

const PRESETS: Record<string, { host: string; port: number; security: SmtpSecurity }> = {
  'Google Workspace / Gmail': { host: 'smtp.gmail.com', port: 587, security: 'starttls' },
  'Microsoft 365 / Outlook': { host: 'smtp.office365.com', port: 587, security: 'starttls' },
  Zoho: { host: 'smtp.zoho.com', port: 465, security: 'tls' },
  'Amazon SES (us-east-1)': { host: 'email-smtp.us-east-1.amazonaws.com', port: 587, security: 'starttls' },
};

const PORT_FOR: Record<SmtpSecurity, number> = { tls: 465, starttls: 587, none: 25 };

function toInput(s: SiteEmailSettings | null): SiteEmailInput {
  return {
    enabled: s?.enabled ?? false,
    host: s?.host ?? '',
    port: s?.port ?? 587,
    security: s?.security ?? 'starttls',
    username: s?.username ?? null,
    password: null,
    fromEmail: s?.fromEmail ?? '',
    fromName: s?.fromName ?? null,
    replyTo: s?.replyTo ?? null,
  };
}

/**
 * Email (SMTP): send this site's mail — chat transcripts, satisfaction
 * surveys, "a visitor asked for a person" notices — from the client's own
 * mail server and address instead of the install's shared sender.
 */
export function EmailSection({ siteId }: { siteId: string }) {
  const me = useAgentStore((s) => s.current?.email ?? '');
  const [saved, setSaved] = useState<SiteEmailSettings | null>(null);
  const [draft, setDraft] = useState<SiteEmailInput>(toInput(null));
  const [encryption, setEncryption] = useState(true);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<'save' | 'test' | 'remove' | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [testTo, setTestTo] = useState('');

  useEffect(() => {
    let live = true;
    void getSiteEmail(siteId)
      .then((r) => {
        if (!live) return;
        setSaved(r.settings);
        setDraft(toInput(r.settings));
        setEncryption(r.encryptionAvailable);
      })
      .catch(() => live && setError('Could not load the email settings.'))
      .finally(() => live && setLoading(false));
    return () => {
      live = false;
    };
  }, [siteId]);
  useEffect(() => setTestTo((t) => t || me), [me]);

  const set = <K extends keyof SiteEmailInput>(k: K, v: SiteEmailInput[K]) => setDraft((d) => ({ ...d, [k]: v }));

  async function run(kind: 'save' | 'test' | 'remove', fn: () => Promise<string>) {
    setBusy(kind);
    setError(null);
    setNotice(null);
    try {
      setNotice(await fn());
    } catch (err) {
      setError(siteEmailError(err));
    } finally {
      setBusy(null);
    }
  }

  if (loading) return <p className={styles.hint}>Loading…</p>;

  const complete = draft.host.trim() && draft.fromEmail.trim();
  return (
    <section className={styles.section}>
      <p className={styles.kicker}>Email (SMTP)</p>
      <p className={styles.hint} style={{ marginTop: 0 }}>
        Send this site’s email — chat transcripts, satisfaction surveys and “a visitor asked for a person”
        notices — from your own mail server and address. Without it, mail goes out from the install’s shared
        sender (Admin → System → Email). Use an app password where your provider offers one.
      </p>
      {saved && (
        <p className={styles.notice} role="status">
          {saved.enabled ? 'On' : 'Off'} · {saved.host}:{saved.port} as {saved.fromEmail}
          {saved.verifiedAt
            ? ` · last test passed ${new Date(saved.verifiedAt).toLocaleString()}`
            : saved.lastError
              ? ` · last test failed: ${saved.lastError}`
              : ' · not tested yet'}
        </p>
      )}
      {!encryption && (
        <p className={styles.error}>
          The server has no CREDENTIALS_ENC_KEY, so a password can’t be stored. Ask the operator to set it.
        </p>
      )}
      <div className={styles.form}>
        <label className={styles.field}>
          <span className={styles.label}>Provider preset</span>
          <select
            className={styles.select}
            value=""
            onChange={(e) => {
              const p = PRESETS[e.target.value];
              if (p) setDraft((d) => ({ ...d, ...p }));
            }}
          >
            <option value="">Fill in for…</option>
            {Object.keys(PRESETS).map((k) => (
              <option key={k} value={k}>
                {k}
              </option>
            ))}
          </select>
        </label>
        <label className={styles.field}>
          <span className={styles.label}>SMTP host</span>
          <input
            className={styles.input}
            value={draft.host}
            placeholder="smtp.example.com"
            onChange={(e) => set('host', e.target.value)}
          />
        </label>
        <label className={styles.field}>
          <span className={styles.label}>Security</span>
          <select
            className={styles.select}
            value={draft.security}
            onChange={(e) => {
              const security = e.target.value as SmtpSecurity;
              setDraft((d) => ({ ...d, security, port: PORT_FOR[security] }));
            }}
          >
            <option value="starttls">STARTTLS (port 587)</option>
            <option value="tls">SSL/TLS (port 465)</option>
            <option value="none">None (trusted network relay only)</option>
          </select>
        </label>
        <label className={styles.field}>
          <span className={styles.label}>Port</span>
          <select className={styles.select} value={draft.port} onChange={(e) => set('port', Number(e.target.value))}>
            {[587, 465, 25, 2525].map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </select>
        </label>
        <label className={styles.field}>
          <span className={styles.label}>Username</span>
          <input
            className={styles.input}
            autoComplete="off"
            value={draft.username ?? ''}
            placeholder="you@example.com"
            onChange={(e) => set('username', e.target.value || null)}
          />
        </label>
        <label className={styles.field}>
          <span className={styles.label}>Password</span>
          <SecretInput
            value={draft.password ?? ''}
            placeholder={saved?.hasPassword ? '•••••••• (saved, leave blank to keep)' : 'App password'}
            onChange={(e) => set('password', e.target.value || null)}
          />
        </label>
        <label className={styles.field}>
          <span className={styles.label}>From address</span>
          <input
            className={styles.input}
            type="email"
            value={draft.fromEmail}
            placeholder="support@example.com"
            onChange={(e) => set('fromEmail', e.target.value)}
          />
        </label>
        <label className={styles.field}>
          <span className={styles.label}>From name</span>
          <input
            className={styles.input}
            maxLength={120}
            value={draft.fromName ?? ''}
            placeholder="Acme Support"
            onChange={(e) => set('fromName', e.target.value || null)}
          />
        </label>
        <label className={styles.field}>
          <span className={styles.label}>Reply-to (optional)</span>
          <input
            className={styles.input}
            type="email"
            value={draft.replyTo ?? ''}
            placeholder="help@example.com"
            onChange={(e) => set('replyTo', e.target.value || null)}
          />
        </label>
        <div className={styles.field} style={{ gridColumn: '1 / -1' }}>
          <label className={styles.checkRow}>
            <input type="checkbox" checked={draft.enabled} onChange={(e) => set('enabled', e.target.checked)} />
            <span className={styles.checkRowText}>
              <span className={styles.label} style={{ textTransform: 'none' }}>
                Send this site’s email through this server
              </span>
              <span className={styles.hint}>Send a test first; switch this on once it arrives.</span>
            </span>
          </label>
        </div>
        <div className={styles.field} style={{ gridColumn: '1 / -1' }}>
          <span className={styles.label}>Send a test to</span>
          <span style={{ display: 'flex', gap: 'var(--inic-space-2)' }}>
            <input
              className={styles.input}
              type="email"
              style={{ flex: 1, minWidth: 0 }}
              value={testTo}
              onChange={(e) => setTestTo(e.target.value)}
            />
            <button
              type="button"
              className={styles.buttonGhost}
              disabled={!complete || !testTo.trim() || busy !== null}
              onClick={() =>
                run('test', async () => {
                  await testSiteEmail(siteId, testTo.trim(), draft);
                  return `Test sent to ${testTo.trim()}. Check the inbox (and spam folder).`;
                })
              }
            >
              {busy === 'test' ? 'Sending…' : 'Send test'}
            </button>
          </span>
        </div>
        <div className={styles.formActions}>
          <button
            type="button"
            className={styles.button}
            disabled={!complete || busy !== null}
            onClick={() =>
              run('save', async () => {
                const next = await saveSiteEmail(siteId, draft);
                setSaved(next);
                setDraft(toInput(next));
                return next.enabled ? 'Saved. This site’s email now goes out through your server.' : 'Saved (switched off).';
              })
            }
          >
            {busy === 'save' ? 'Saving…' : 'Save →'}
          </button>
          {saved && (
            <button
              type="button"
              className={styles.buttonDanger}
              disabled={busy !== null}
              onClick={async () => {
                if (!(await confirmDialog({ title: 'Remove these email settings?', message: 'This site’s email goes back to the shared sender.', confirmLabel: 'Remove' }))) return;
                void run('remove', async () => {
                  await deleteSiteEmail(siteId);
                  setSaved(null);
                  setDraft(toInput(null));
                  return 'Removed. This site uses the shared sender again.';
                });
              }}
            >
              remove
            </button>
          )}
        </div>
      </div>
      {notice && (
        <p className={styles.notice} role="status">
          {notice}
        </p>
      )}
      {error && <p className={styles.error} role="alert">{error}</p>}
    </section>
  );
}
